import React, { useState, useMemo, useEffect, useCallback } from "react";
import "./style.scss";
import dock_left from "../../common/icons/dock_left.svg";
import { NavLink, useNavigate } from "react-router-dom";
import Icon from "../../common/ui/Icon";
import tteld from "../../../assets/images/tteld.svg";
import evoeld from "../../../assets/images/evoeld.svg";
import ontime from "../../../assets/images/ontime.svg";
import zippyeld from "../../../assets/images/zippyeld.svg";
import fiveeld from "../../../assets/images/fiveeld.svg";
import rulereld from "../../../assets/images/rulereld.svg";
import fleetvision from "../../../assets/images/fleetvision.svg";
import logfleet from "../../../assets/images/logfleet.svg";
import zeelog from "../../../assets/images/zeelog.svg";
import { TIconsNames } from "../../common/iconSets";
import TextTitle from "../../common/ui/typography/TextTitle";
import TextCaption from "../../common/ui/typography/TextCaption";
import { useGlobalStore } from "../../../store";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import TextBody from "../../common/ui/typography/TextBody";
import { TDropdownElement } from "../../overview/types";
import Button from "../../common/ui/buttons/Button";
import { useQuery } from "react-query";
import { statistics } from "../../../api/statistics";
import SetTruckLimitModal from "../../overview/components/TruckLimitModal/SetTruckLimitModal";
import moment from "moment";
import DueDateModal from "../../overview/components/DueDateModal/DueDateModal";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
  const navigate = useNavigate();
  const sidebarMobileOpen = useGlobalStore((state) => state.sidebarMobileOpen);
  const selectedCompany = useGlobalStore((state) => state.selectedCompany);
  const setSelectedCompany = useGlobalStore(
    (state) => state.setSelectedCompany
  );
  const toggleMobileSidebar = useGlobalStore(
    (state) => state.toggleMobileSidebar
  );

  const {
    data: truckLimit,
    isLoading,
    refetch,
  } = useQuery(["trucklimit", selectedCompany], () =>
    statistics.getTruckLimit(selectedCompany)
  );

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const sidebarItems = useMemo<
    {
      link: string;
      title: string;
      icon: { active: TIconsNames; inActive: TIconsNames };
      show: boolean;
    }[]
  >(
    () => [
      {
        link: "/",
        title: "Overview",
        icon: { active: "view-desktop-filled", inActive: "view-desktop" },
        show: true,
      },
      {
        link: "/feedbacks",
        title: "Feedbacks",
        icon: { active: "comment-filled", inActive: "comment" },
        show: selectedCompany !== "all",
      },
      {
        link: "/heatmap",
        title: "Heatmap",
        icon: { active: "data-scatter-filled", inActive: "data-scatter" },
        show: true,
      },
      {
        link: "/force-update",
        title: "Force update",
        icon: { active: "history-filled", inActive: "history" },
        show: true,
      },
      {
        link: "/referral",
        title: "Referral",
        icon: { active: "referral-filled", inActive: "referral" },
        show: true,
      },
    ],
    [selectedCompany]
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1280);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1280);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile && sidebarMobileOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isMobile, sidebarMobileOpen]);

  const sidebarOpenCheck = isMobile ? sidebarMobileOpen : sidebarOpen;

  const closeSidebarOnMobile = () => {
    if (isMobile) {
      toggleMobileSidebar();
    }
  };

  const logoMap = [
    { key: "tteld", logo: tteld },
    { key: "evoeld", logo: evoeld },
    { key: "ontime-logs", logo: ontime },
    { key: "zippyeld", logo: zippyeld },
    { key: "fiveeld", logo: fiveeld },
    { key: "rulereld", logo: rulereld },
    { key: "fleetvision", logo: fleetvision },
    { key: "logfleet", logo: logfleet },
    { key: "zeelog", logo: zeelog },
  ];

  const getLogo = (company: string) =>
    logoMap.find(({ key }) => company.includes(key))?.logo ?? tteld;

  const logo = useMemo(() => getLogo(selectedCompany), [selectedCompany]);

  const onLogout = () => {
    localStorage.clear();
    navigate("auth/login");
  };
  const toggle = () => setDropdownOpen((prevState) => !prevState);
  const dropdownElements: TDropdownElement[] = [
    { icon: "grid-filled", title: "All Companies", value: "all" },
    { icon: "tteld-logo", title: "TT ELD", value: "tteld" },
    { icon: "ontime-logo", title: "OnTime Logs", value: "ontime-logs" },
    { icon: "evo-logo", title: "EVO ELD", value: "evoeld" },
    { icon: "zippy-logo", title: "ZIPPY ELD", value: "zippyeld" },
    { icon: "ruler-logo", title: "Ruler ELD", value: "rulereld" },
    { icon: "five-logo", title: "Five ELD", value: "fiveeld" },
    { icon: "fleet-logo", title: "FLEET VISION", value: "fleetvision" },
    { icon: "logfleet-logo", title: "LOG FLEET", value: "logfleet" },
    { icon: "zeelog", title: "Zeelog", value: "zeelog" },
  ];

  const getProgressColor = (value: number, max: number): string => {
    if (value > max) return "red";
    if (value > max * 0.9) return "orange";
    return "blue";
  };

  return (
    <>
      {truckLimit && (
        <SetTruckLimitModal
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen(!isModalOpen)}
          initialLimit={400}
          onSave={(newLimit) => {
            refetch();
          }}
          truckLimit={truckLimit.quantity}
        />
      )}
      {truckLimit && isDueDateModalOpen && (
        <DueDateModal
          initialLimit={400}
          isOpen={isDueDateModalOpen}
          toggle={() => setIsDueDateModalOpen(!isDueDateModalOpen)}
          dueDate={truckLimit.paymentDueDay || 0}
          onSave={() => refetch()}
        />
      )}
      {sidebarOpenCheck && (
        <div className="sidebar-overlay" onClick={closeSidebarOnMobile} />
      )}
      <div className={`sidebar ${sidebarOpenCheck ? "open" : "closed"}`}>
        <div className="sidebarHeader">
          <Dropdown
            isOpen={dropdownOpen}
            toggle={toggle}
            className="sidebar-dropdown"
          >
            <DropdownToggle className="sidebar-dropdown__toggle" caret>
              {sidebarOpenCheck &&
                (selectedCompany === "all" ? (
                  <div
                    style={{
                      color: "#212529",
                      display: "flex",
                      gap: "12px",
                      verticalAlign: "center",
                    }}
                  >
                    <Icon icon="grid-filled" />
                    <TextTitle level="3">All Companies</TextTitle>
                  </div>
                ) : (
                  <img
                    src={logo}
                    alt="logo"
                    className="sidebar-dropdown__logo"
                  />
                ))}
              <div
                style={{
                  cursor: "pointer",
                  marginLeft: "auto",
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.1s ease-in-out",
                }}
              >
                <Icon
                  icon="chevron-down-blue"
                  className="sidebar-dropdown__icon"
                />
              </div>
            </DropdownToggle>
            <DropdownMenu className="sidebar-dropdown__menu">
              {dropdownElements.map((el) => {
                return (
                  <DropdownItem
                    className="sidebar-dropdown__menu-item"
                    onClick={() => setSelectedCompany(el.value)}
                    active={el.value === selectedCompany}
                  >
                    <Icon
                      icon={el.icon}
                      className="sidebar-dropdown__menu-icon"
                      size={"20px"}
                    />
                    <TextBody level="1" className="sidebar-dropdown__menu-text">
                      {el.title}
                    </TextBody>
                    {el.value === selectedCompany && (
                      <Icon
                        icon="checkmark-circle-filled-blue"
                        className="sidebar-dropdown__menu-item-icon"
                      />
                    )}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </Dropdown>
        </div>
        {sidebarOpenCheck && (
          <div className="sidebar__profile">
            <Icon icon="profile" />
            <div className="sidebar__profile--title">
              <TextTitle level="4">TT ELD</TextTitle>
              <TextCaption
                level="1"
                className="sidebar__profile--title-caption"
              >
                {user.first_name || ""} {user.second_name || ""}
              </TextCaption>
            </div>
          </div>
        )}
        {sidebarItems.map(
          (item) =>
            item.show && (
              <div className="sidebarMenu" key={item.link}>
                <div className="sidebarSubMenu">
                  <NavLink
                    className={({ isActive }) =>
                      isActive
                        ? "sidebarSubMenuHeadActive"
                        : "sidebarSubMenuHead"
                    }
                    to={item.link}
                    onClick={closeSidebarOnMobile}
                    style={
                      !sidebarOpenCheck
                        ? { justifyContent: "center", width: "44px" }
                        : undefined
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          icon={
                            isActive ? item.icon.active : item.icon.inActive
                          }
                        />
                        {sidebarOpenCheck && item.title}
                      </>
                    )}
                  </NavLink>
                </div>
              </div>
            )
        )}

        <div className="sidebarMenu" style={{ marginTop: "auto" }}>
          {sidebarOpenCheck &&
            !isMobile &&
            !isLoading &&
            (selectedCompany === "rulereld" ||
              selectedCompany === "fleetvision" ||
              selectedCompany === "uateld") && (
              <>
                <div
                  className="truck-limit"
                  onClick={() => setIsDueDateModalOpen(true)}
                >
                  <div className="d-flex w-100 justify-content-between">
                    <div className="body-2">Payment due date</div>
                    <div className="body-2">
                      {truckLimit.paymentDueDay && moment().date(truckLimit.paymentDueDay).format("Do")}
                    </div>
                  </div>

                  {truckLimit.paymentDueDay && <div className="caption-1">
                    {Math.abs(
                      moment()
                        .date(truckLimit.paymentDueDay)
                        .diff(moment(), "days")
                    )}{" "}
                    {Math.abs(
                      moment()
                        .date(truckLimit.paymentDueDay)
                        .diff(moment(), "days")
                    ) > 1
                      ? "days"
                      : "day"}{" "}
                    left
                  </div>}
                </div>
                <div className="truck-limit mt-2">
                  <div className="truck-limit--header">
                    <TextBody level="1">Truck Limit</TextBody>
                    <TextBody level="1" className="d-flex">
                      {truckLimit.activeVehicleTotal}/
                      <TextTitle level="4">{truckLimit.quantity}</TextTitle>
                    </TextBody>
                  </div>

                  <div className="truck-limit--progress">
                    <div className="progress-bar-wrapper">
                      <div
                        className={`progress-bar ${getProgressColor(truckLimit.totalTrucks, truckLimit.quantity)}`}
                        style={{
                          width: `${Math.min((truckLimit.totalTrucks / truckLimit.quantity) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>

                  <Button
                    className="truck-limit--button"
                    color={
                      truckLimit.totalTrucks > truckLimit.quantity
                        ? "red"
                        : undefined
                    }
                    onClick={() => setIsModalOpen(true)}
                  >
                    Change limit
                  </Button>
                </div>
              </>
            )}
        </div>

        <div
          className="sidebarMenu"
          style={{ marginBottom: "20px" }}
          onClick={onLogout}
        >
          <div className="sidebarSubMenu">
            <div className="sidebarSubMenuHead">
              <Icon icon="logout" />
              {sidebarOpenCheck && "Logout"}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
